import { ref, computed } from 'vue'
import type { User, Organisation } from '@/types/auth'
import {
  fetchCsrfCookie,
  fetchCurrentUser,
  login as apiLogin,
  logout as apiLogout,
  register as apiRegister,
} from '@/api/auth'
import type { LoginPayload, RegisterPayload } from '@/api/auth'

const user = ref<User | null>(null)
const isLoading = ref(false)

// True once the current auth state (authenticated or not) has been resolved
// against the server for this page load. Route guards use this to avoid
// re-probing /auth/user on every client-side navigation — the fetch only
// needs to happen once per hard page load, not once per guarded route.
const authResolved = ref(false)

// Single-flight guard: concurrent callers (e.g. the router guard resolving
// several routes in quick succession) await the same in-flight request
// instead of firing duplicate /auth/user calls.
let inFlightFetch: Promise<void> | null = null

const isAuthenticated = computed(() => user.value !== null)
const isEmailVerified = computed(() => !!user.value?.email_verified_at)
const isOrganizer = computed(() => (user.value?.organisations ?? []).length > 0)
const currentOrganisation = computed<Organisation | null>(() => user.value?.organisations[0] ?? null)

async function fetchUser(): Promise<void> {
  if (inFlightFetch) {
    return inFlightFetch
  }

  inFlightFetch = (async () => {
    isLoading.value = true
    try {
      await fetchCsrfCookie()
      user.value = await fetchCurrentUser()
    } catch {
      user.value = null
    } finally {
      isLoading.value = false
      authResolved.value = true
      inFlightFetch = null
    }
  })()

  return inFlightFetch
}

async function login(payload: LoginPayload): Promise<User> {
  await fetchCsrfCookie()
  user.value = await apiLogin(payload)
  return user.value
}

async function logout(): Promise<void> {
  try {
    await fetchCsrfCookie()
    await apiLogout()
  } finally {
    user.value = null
  }
}

async function register(payload: RegisterPayload): Promise<User> {
  await fetchCsrfCookie()
  user.value = await apiRegister(payload)
  return user.value
}

export function useAuth() {
  return {
    user,
    isAuthenticated,
    isEmailVerified,
    isOrganizer,
    currentOrganisation,
    isLoading,
    authResolved,
    fetchUser,
    login,
    logout,
    register,
  }
}
